import React from 'react';
import { useTranslation } from 'react-i18next';

interface EmptyStateProps {
  type: 'no-results' | 'no-matchees' | 'error';
  message: string;
  onRetry?: () => void;
  errorType?: 'network' | 'server' | 'unknown' | null;
  retryCount?: number;
}

const EmptyState: React.FC<EmptyStateProps> = ({ 
  type, 
  message, 
  onRetry, 
  errorType = null, 
  retryCount = 0 
}) => {
  const { t } = useTranslation();

  const getIcon = () => {
    switch (type) {
      case 'no-results':
        return (
          <svg
            className="w-10 h-10 sm:w-12 sm:h-12 text-sangati-orange mx-auto mb-3 sm:mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        );
      case 'no-matchees':
        return (
          <svg
            className="w-10 h-10 sm:w-12 sm:h-12 text-neutral-grey mx-auto mb-3 sm:mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        );
      case 'error':
        return (
          <svg
            className="w-10 h-10 sm:w-12 sm:h-12 text-sangati-orange mx-auto mb-3 sm:mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const getTextColor = () => {
    switch (type) {
      case 'no-results':
        return 'text-sangati-orange';
      case 'no-matchees':
        return 'text-neutral-grey';
      case 'error':
        return 'text-sangati-orange';
      default:
        return 'text-neutral-grey';
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-8 sm:py-12 px-4 font-texgyrepagella">
      {getIcon()}
      
      <h3 className={`text-16 sm:text-18 font-medium mb-2 text-center ${getTextColor()}`}>
        {message}
      </h3>
      
      {type === 'no-results' && (
        <p className="text-14 sm:text-16 text-neutral-grey text-center mb-4">
          {t('tryDifferentSearch', 'Try searching for a different handle')}
        </p>
      )}
      
      {type === 'no-matchees' && (
        <p className="text-14 sm:text-16 text-neutral-grey text-center mb-4">
          {t('noMatcheesDescription', 'There are no matchees available at the moment')}
        </p>
      )}
      
      {type === 'error' && (
        <div className="text-center max-w-sm">
          <p className="text-14 sm:text-16 text-neutral-grey mb-4">
            {errorType === 'network' 
              ? t('networkErrorDescription', 'Please check your internet connection and try again')
              : errorType === 'server'
              ? t('serverErrorDescription', 'Our servers are experiencing issues. Please try again later')
              : t('errorDescription', 'Something went wrong while loading matchees')
            }
          </p>
          
          {retryCount > 0 && (
            <p className="text-12 sm:text-14 text-neutral-grey mb-3">
              {t('retryAttempt', 'Retry attempt: {{count}}/3', { count: retryCount })}
            </p>
          )}
          
          {onRetry && (
            <button
              onClick={onRetry}
              className={`
                px-4 py-2 sm:px-6 sm:py-3 bg-sangati-green text-white rounded-lg 
                font-texgyrepagella font-bold text-14 sm:text-16 shadow-sangati
                transition-all duration-200 uppercase
                ${retryCount >= 3 
                  ? 'opacity-50 cursor-not-allowed bg-neutral-grey' 
                  : 'hover:bg-sangati-dark active:scale-95'
                }
              `}
              disabled={retryCount >= 3}
            >
              {retryCount >= 3 
                ? t('maxRetriesReached', 'Max retries reached')
                : t('retryAction', 'Retry')
              }
            </button>
          )}
          
          {retryCount >= 3 && (
            <p className="text-12 sm:text-14 text-neutral-grey mt-3">
              {t('refreshPageSuggestion', 'Please refresh the page to try again')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default EmptyState;